import config from "_src/config";
import output from "_src/engine/output";
import events from "_src/engine/events";
import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import { primitiveType } from "_src/engine/constants";
import minmaxUtils from "_src/engine/utility/minmax";

import {vec3, mat4} from "_src/libs/gl-matrix";

import workspace from "_src/workspace";
import DefaultShader from "_src/shaders/default/default_shader";
import AbstractManipulator from "_src/client/manipulators/abstract_manipulator";
import selector from "_src/manipulators/libs/selector";
import wasmHelper from "_src/client/manipulators/wasm_helper"
import { screenUtils } from "_src/client/utils";
import { colorUtils } from "_src/utils";
import { shadingType, stepType, objectType } from "_src/constants";
import windowManager from "_src/client/window_manager";
import history from "_src/history/manager";
import appState from "_src/app_state";
import Animation from "_src/client/animation";

const SPHERE_COLOR = [0.5, 0.0, 0.5];
const DEFAULT_SPHERE_SIZE = 10;
const HOVERED_SPHERE_SIZE = 15;

const _VEC3_TMP = vec3.create();
const _VEC3_TMP2 = vec3.create();
const _VEC3_TMP3 = vec3.create();
const _MAT4_TMP = mat4.create();

class Pick {
  constructor() {
    this.drawable = null;
    this.hash = null;
    this.size = null;
  }
}

export default class Align extends AbstractManipulator {
  constructor() {
    super();

    this._sphereGeometry = primitiveFactory.create(primitiveType.SPHERE);
    this._sphereShader = new DefaultShader({
      shading: shadingType.SMOOTH,
      specularColor: [0,0,0],
      depthTest: false,
    });
    this._aligneePicks = [];
    this._alignerPicks = [];
    this._lastReportedHover = {object: null};
    this._lastReportedPopup = {object: null};
    this._lastReportedSelection = {object: null};
    this._dragging = {object: null, isActive: false};
    this._lonelyPoint = {object: null};

    this._animation = new Animation();
  }

  _onStart() {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const camera = mainWindow.renderGraph.getWorkspaceScene().camera;

    windowManager.ALIGNEE = windowManager.createWindow([0,0,0,0], camera.type);
    windowManager.ALIGNER = windowManager.createWindow([0,0,0,0], camera.type);

    events.emit("alignStart", {"aligneeWindow": windowManager.ALIGNEE, "alignerWindow": windowManager.ALIGNER});
    if (config.clientAlign) {
      wasmHelper.init();
    }
  }

  _onStop() {

    const mainViewport = windowManager.getWindow(windowManager.MAIN).getViewport();
    const alignViewport = windowManager.getWindow(windowManager.ALIGNEE).getViewport();

    windowManager.updateWindowViewport(windowManager.MAIN, [0, 0, mainViewport[2] + alignViewport[2], mainViewport[3]]);

    windowManager.deleteWindow(windowManager.ALIGNEE);
    windowManager.deleteWindow(windowManager.ALIGNER);

    events.emit("visibility_changed");
    events.emit("canvas_resize");
    wasmHelper.cleanup();
  }

  _applyMatrix(matrix) {
    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const objects = aligneeWindow.objects;

    for (const object of objects) {
      mat4.multiply(object.model.modelMatrix, matrix, object.model.modelMatrix);
      object.model.setMatrix(object.model.modelMatrix);
      object.model.updateMatrix();
    }
  }

  _animateMatrix(matrix, screenRect) {
    return new Promise((resolve) => {
      const mainWindow = windowManager.getWindow(windowManager.MAIN);
      const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
      const alignerWindow = windowManager.getWindow(windowManager.ALIGNEE);

      const resultingMinmax = new Array(6);
      minmaxUtils.reset(resultingMinmax);
      for (const object of aligneeWindow.objects.concat(alignerWindow.objects)) {
        const objectFinalMinmax = minmaxUtils.recomputedFromTransform([], object.getWorldMinmax(), matrix);
        minmaxUtils.expandMinmax(resultingMinmax, objectFinalMinmax);
      }

      const modelToInitialMatrixAndDelta = new Map();
      for (const object of aligneeWindow.objects) {
        const modelMatrix = object.model.modelMatrix;
        const initialMatrix = mat4.clone(modelMatrix);
        const finalMatrix = mat4.multiply(mat4.create(), matrix, modelMatrix);
        const delta = mat4.subtract(mat4.create(), finalMatrix, initialMatrix);
        modelToInitialMatrixAndDelta.set(object.model, {initialMatrix, delta});
      }

      const affectedObjects = aligneeWindow.objects.slice();
      const onUpdate = (value) => {
        for (const object of affectedObjects) {
          const initialMatrixAndDelta = modelToInitialMatrixAndDelta.get(object.model);
          const currentMatrix = mat4.multiplyScalarAndAdd(_MAT4_TMP, initialMatrixAndDelta.initialMatrix, initialMatrixAndDelta.delta, value);
          object.move(currentMatrix);

          events.emit("shallow_object_update", object);
        }

        mainWindow.renderGraph.requestFrame();
      };

      const onFinish = () => {
        for (const object of affectedObjects) {
          const initialMatrixAndDelta = modelToInitialMatrixAndDelta.get(object.model);
          const finalMatrix = mat4.multiply(_MAT4_TMP, matrix, initialMatrixAndDelta.initialMatrix);
          object.move(finalMatrix, true);
          object.model.updateMatrix();
          events.emit("object_moved", object);
        }

        mainWindow.renderGraph.requestFrame();

        resolve();
      };

      mainWindow.cameraControl.moveToMinmax(resultingMinmax, screenRect, this._animation, () => {}, onUpdate, onFinish, 2.0);
    });
  }

  align(matrix, screenRect) {
    this.reset();

    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    for (const object of aligneeWindow.objects) {
      history.writeObjectToCurrentStep(object);
    }

    const alignMatrix = mat4.clone(matrix);
    mat4.transpose(alignMatrix, alignMatrix);

    this._animateMatrix(alignMatrix, screenRect);

    //NOTE: animation will be executed from original matrix, but now we apply matrix for history write
    this._applyMatrix(alignMatrix);

    history.addStep(stepType.TRANSFORM);
    events.emit("objects_updated", aligneeWindow.objects);
    events.emit("alignFinish");
  }

  updateAnimation(dt) {
    this._animation.update(dt);
  }

  _getAlignData(payload) {
    const [hornFromPoints, hornToPoints] = this._getPointsCoords();

    if (hornFromPoints.length < 3) {
      output.error(output.API_ERROR, "Can't apply align. Not enough pairs to align.");
      return false;
    }

    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);
    const ICPFromObjectUUIDs = aligneeWindow.objects.map(object => object.getUUID());
    const ICPToObjectUUIDs = alignerWindow.objects.map(object => object.getUUID());

    payload["hornFromPoints"] = hornFromPoints;
    payload["hornToPoints"] = hornToPoints;
    payload["ICPFromObjects"] = ICPFromObjectUUIDs;
    payload["ICPToObjects"] = ICPToObjectUUIDs;

    return true;
  }

  async _getFilesData(payload) {
    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);

    const slices = new Set();
    const slicesPromises = [];
    const projectFiles = [];
    for (const objectGroup of [aligneeWindow.objects, alignerWindow.objects]) {
      for (const object of objectGroup) {
        if (object.type === objectType.CAD) {
          workspace.getObjectFiles(object, projectFiles); //this is no needed but algorithm asks
          slices.add(workspace.getCadSlice(object))
        } else {
          workspace.getObjectFiles(object, projectFiles);

          let framesProperty;
          if (object.type === objectType.SURFELS) {
            framesProperty = await workspace.getSurfelsObjectFrameGeometrySlices(object);
          } else {
            framesProperty = workspace.getObjectFramesProperty(object);
          }

          const isScan = object.type === objectType.SCAN || object.type === objectType.SURFELS;
          const containsKeyOrTexturedFrames = () => {
            for (const frameProperty of framesProperty) {
              if (frameProperty.getValue("keyframe") || frameProperty.getValue("textured")) {
                return true;
              }
            }

            return false;
          }
          const keepAllFrames = !isScan || !containsKeyOrTexturedFrames();

          for (const frameProperty of framesProperty) {
            const containsTextures = frameProperty.getValue("textured");
            const keyframe = frameProperty.getValue("keyframe");

            if (!keepAllFrames && !containsTextures && !keyframe) {
              continue;
            }

            const slicesPathPromise = workspace.getFrameGeometrySlices(frameProperty).then((slicesPath) => {
              for (const path of slicesPath) {
                slices.add(path);
              }
            });
            slicesPromises.push(slicesPathPromise);
          }
        }
      }
    }

    await Promise.all(slicesPromises);

    payload["slices"] = Array.from(slices);
    payload["projectFiles"] = projectFiles;
  }

  async directAlign(rectNDC) {
    const payload = {};
    if (!this._getAlignData(payload)) {
      return;
    }

    this.reset();

    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    for (const object of aligneeWindow.objects) {
      history.writeObjectToCurrentStep(object);
    }

    const hornMatrix = await wasmHelper.horn(payload["hornFromPoints"], payload["hornToPoints"]);
    mat4.transpose(hornMatrix, hornMatrix);

    const animationPromise = this._animateMatrix(hornMatrix, rectNDC);

    //NOTE: horn animation will be executed from original matrix, but now we apply horn matrix for icp step
    this._applyMatrix(hornMatrix);

    const ICPMatrix = await wasmHelper.ICP(payload["ICPFromObjects"], payload["ICPToObjects"]);
    mat4.transpose(ICPMatrix, ICPMatrix);

    await animationPromise;
    this._animateMatrix(ICPMatrix, rectNDC);

    //NOTE: ICP animation will be executed from horn-transformed matrix, but now we apply ICP matrix for history write
    this._applyMatrix(ICPMatrix);

    history.addStep(stepType.TRANSFORM);
    events.emit("objects_updated", aligneeWindow.objects);
    events.emit("alignFinish");
  }

  async sendAlignData() {
    const payload = {};
    if (!this._getAlignData(payload)) {
      return;
    }
    await this._getFilesData(payload);
    events.emit("alignApply", payload);
  }

  reset() {
    this._lastReportedHover = {object: null};
    this._lastReportedPopup = {object: null};
    this._lastReportedSelection = {object: null};
    this._dragging = {object: null, isActive: false};
    this._lonelyPoint = {object: null};

    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);

    if (!aligneeWindow || !alignerWindow) {
      return;
    }

    const aligneeScene = aligneeWindow.renderGraph.getInterfaceScene();
    const alignerScene = alignerWindow.renderGraph.getInterfaceScene();
    for (const pick of this._aligneePicks) {
      aligneeScene.remove(pick.drawable);
    }
    this._aligneePicks.length = 0;

    for (const pick of this._alignerPicks) {
      alignerScene.remove(pick.drawable);
    }
    this._alignerPicks.length = 0;

    aligneeWindow.renderGraph.requestFrame();
    alignerWindow.renderGraph.requestFrame();
    this._reportPoints();
  }

  removePointsPair(ind) {
    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);

    const aligneeScene = aligneeWindow.renderGraph.getInterfaceScene();
    const alignerScene = alignerWindow.renderGraph.getInterfaceScene();

    const aligneePick = this._aligneePicks.find(pick => pick.hash === ind);
    const alignerPick = this._alignerPicks.find(pick => pick.hash === ind);

    if (!aligneePick || !alignerPick) {
      output.error(output.API_ERROR, "Can't remove align points pair. There no pair with index: " + ind);
      return;
    }

    aligneeScene.remove(aligneePick.drawable);
    alignerScene.remove(alignerPick.drawable);

    this._processHover(null);
    this._disablePopupHandler();
    this._alignerPicks.splice(this._alignerPicks.indexOf(alignerPick),1);
    this._aligneePicks.splice(this._aligneePicks.indexOf(aligneePick),1);
    aligneeWindow.renderGraph.requestFrame();
    alignerWindow.renderGraph.requestFrame();

    this._reportPoints();
  }

  removePoint(ind, isAligner) {
    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);

    const thisWindow = isAligner ? alignerWindow : aligneeWindow;
    const anotherWindow = isAligner ? aligneeWindow : alignerWindow;

    const thisPicks = isAligner ? this._alignerPicks : this._aligneePicks;
    const anotherPicks = isAligner ? this._aligneePicks : this._alignerPicks;

    const thisScene = thisWindow.renderGraph.getInterfaceScene();
    const thisPick = thisPicks.find(pick => pick.hash === ind);
    if (!thisPick) {
      output.error(output.API_ERROR, "Can't remove align point. There no point with index: " + ind);
      return;
    }

    this._processHover(null);
    this._disablePopupHandler();

    thisScene.remove(thisPick.drawable);
    thisPicks.splice(thisPicks.indexOf(thisPick), 1);

    if (this._lonelyPoint.object) {
      this._lonelyPoint.object = null;
    } else {
      const anotherPick = anotherPicks.find(pick => pick.hash === ind);
      if (!anotherPick) {
        output.error(output.DEVELOPERS_ERROR, "Can't find lonely point after deletion. There no point with index: " + ind);
        return;
      }

      this._lonelyPoint.object = anotherPick.drawable;
      this._lonelyPoint.isAligner = anotherWindow === alignerWindow;
    }

    thisWindow.renderGraph.requestFrame();

    this._reportPoints();
  }

  resizePointsPair(ind, newSize) {
    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);

    const aligneePick = this._aligneePicks.find(pick => pick.hash === ind);
    const alignerPick = this._alignerPicks.find(pick => pick.hash === ind);

    if (!aligneePick || !alignerPick) {
      output.error(output.API_ERROR, "Can't resize align points pair. There no pair with index: " + ind);
      return;
    }

    if (aligneePick && aligneePick.drawable !== this._lastReportedHover.object && aligneePick.drawable !== this._lastReportedSelection.object && aligneePick.drawable !== this._lastReportedPopup.object) {
      aligneePick.size = newSize;
      aligneeWindow.renderGraph.requestFrame();
    }

    if (alignerPick && alignerPick.drawable !== this._lastReportedHover.object && alignerPick.drawable !== this._lastReportedSelection.object && alignerPick.drawable !== this._lastReportedPopup.object) {
      alignerPick.size = newSize;
      alignerWindow.renderGraph.requestFrame();
    }

    this._resizePoints();
  }

  _reportPoints() {
    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);

    if (!aligneeWindow || !alignerWindow) {
      return;
    }

    const [leftPoints, rightPoints] = this._getPointsData();

    const aligneeCamera = aligneeWindow.renderGraph.getInterfaceScene().camera;
    const alignerCamera = alignerWindow.renderGraph.getInterfaceScene().camera;

    const leftScreenPoints = [];
    for (let point of leftPoints) {
      const screenPoint = aligneeCamera.toScreenSpace(point.position, _VEC3_TMP3);
      const {x, y} = screenUtils.fromScreen(aligneeWindow.getViewport(), aligneeCamera.viewport, screenPoint[0], screenPoint[1]);
      const physicalPoint = [x, y];
      leftScreenPoints[point.hash] = {position: screenUtils.physicalPointToVirtual(physicalPoint, physicalPoint), color: point.color};
    }

    const rightScreenPoints = [];
    for (let point of rightPoints) {
      const screenPoint = alignerCamera.toScreenSpace(point.position, _VEC3_TMP3);
      const {x, y} = screenUtils.fromScreen(alignerWindow.getViewport(), alignerCamera.viewport, screenPoint[0], screenPoint[1]);
      const physicalPoint = [x, y];
      rightScreenPoints[point.hash] = {position: screenUtils.physicalPointToVirtual(physicalPoint, physicalPoint), color: point.color};
    }

    appState.setStateValue("alignPoints", {"leftAlignPoints": leftScreenPoints, "rightAlignPoints": rightScreenPoints});
  }

  enablePointPopup(screenX, screenY) {
    this._disablePopupHandler();

    const pick = this._getPick(screenX, screenY);
    if (pick.object !== null) {
      this._enablePopupHandler(pick.object, pick.isAligner);
    }

    this._dragging.object = null;
    this._dragging.isActive = false;
  }

  _mouseDownHandler({screenX, screenY}) {
    const pick = this._getPick(screenX, screenY);

    if (pick.object !== null) {
      this._dragging.object = pick.object;
      this._dragging.isActive = false;
      this._dragging.isAligner = pick.isAligner;
    }
  }

  _mouseUpHandler({screenX, screenY}) {
    this._disablePopupHandler();

    if (!this._dragging.isActive) {
      const pick = this._getPick(screenX, screenY);

      if (pick.object === null) {
        if (this._lastReportedSelection.object === null) {
          this._pickHandler(screenX, screenY);
        } else {
          this._moveHandler(screenX, screenY);
        }
      } else {
        if (pick.object === this._lonelyPoint.object) {
          const picks = this._lonelyPoint.isAligner ? this._alignerPicks : this._aligneePicks;
          const lonelyPick = picks.find(pick => pick.drawable === this._lonelyPoint.object);
          this.removePoint(lonelyPick.hash, this._lonelyPoint.isAligner);
        } else {
          this._selectionHandler(pick.object, pick.isAligner);
        }
      }
    }

    this._dragging.object = null;
    this._dragging.isActive = false;
    this._processHover(null);
  }

  _mouseMoveHandler({screenX, screenY}) {
    if (this._dragging.object) {
      this._dragHandler(screenX, screenY);
    } else {
      this._hoverHandler(screenX, screenY);
    }
  }

  _dragHandler(screenX, screenY) {
    const dragWindow = this._dragging.isAligner ?
      windowManager.getWindow(windowManager.ALIGNER):
      windowManager.getWindow(windowManager.ALIGNEE);

    if (!dragWindow.isInViewport(screenX, screenY)) {
      return;
    }

    const camera = dragWindow.renderGraph.getWorkspaceScene().camera;
    const sphereCenter = _VEC3_TMP2;
    dragWindow.getPixelWorldPoint(screenX, screenY, camera, sphereCenter).then(res => {
      if (!res) {
        return;
      }

      this._dragging.isActive = true;

      const selectedObject = this._dragging.object;
      selectedObject.setTranslation(...sphereCenter);
      this._resizePoints();
      this._reportPoints();
      dragWindow.renderGraph.requestFrame();
    });
  }

  _hoverHandler(screenX, screenY) {
    const pick = this._getPick(screenX, screenY);
    this._processHover(pick.object, pick.isAligner);
  }

  _getPick(screenX, screenY) {
    let thisWindow = null;
    let thisPicks = null;
    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);

    if (alignerWindow.isInViewport(screenX, screenY)) {
      thisWindow = alignerWindow;
      thisPicks = this._alignerPicks;
    } else if (aligneeWindow.isInViewport(screenX, screenY)) {
      thisWindow = aligneeWindow;
      thisPicks = this._aligneePicks;
    }

    if (!thisWindow) {
      return {object: null, isAligner: false};
    }

    const isAligner = thisWindow === alignerWindow;

    const picksScene = thisWindow.renderGraph.getInterfaceScene();
    const picks = [];
    for (const pick of thisPicks) {
      picks.push(pick.drawable);
    }
    const camera = picksScene.camera;
    const {x, y} = screenUtils.toScreen(thisWindow.getViewport(), camera.viewport, screenX, screenY);

    const intersectedObject = selector.findClosestIntersection(null, x, y, camera, picks);
    return {object: intersectedObject, isAligner: isAligner};
  }

  _cameraMoveHandler(camera) {
    const updated = this._updateScales(camera);
    if (updated) {
      this._reportPoints();
    }
  }

  _processHover(object, isAligner) {
    if (this._lastReportedHover.object !== object && (!this._lonelyPoint.object || !object || this._lonelyPoint.object === object)) {
      if (this._lastReportedHover.object !== null &&
        this._lastReportedHover.object !== this._lastReportedPopup.object &&
        this._lastReportedHover.object !== this._lastReportedSelection.object) {

        const picks = this._lastReportedHover.isAligner ? this._alignerPicks : this._aligneePicks;
        const outgoingPick = picks.find(pick => pick.drawable === this._lastReportedHover.object);

        events.emit("alignPointHovered", {"isAligner": this._lastReportedHover.isAligner, "index": outgoingPick.hash, "isHovered": false, "isContextMenuRequested": false, "isSelected": false});
        outgoingPick.size = DEFAULT_SPHERE_SIZE;
      }

      if (object !== null && object !== this._lastReportedPopup.object && object !== this._lastReportedSelection.object) {
        const picks = isAligner ? this._alignerPicks : this._aligneePicks;
        const incomingPick = picks.find(pick => pick.drawable === object);

        events.emit("alignPointHovered", {"isAligner": isAligner, "index": incomingPick.hash, "isHovered": true, "isContextMenuRequested": false, "isSelected": false});
        incomingPick.size = HOVERED_SPHERE_SIZE;
      }

      this._lastReportedHover.object = object;
      this._lastReportedHover.isAligner = isAligner;

      this._resizePoints();

      windowManager.getWindow(isAligner ? windowManager.ALIGNER : windowManager.ALIGNEE).renderGraph.requestFrame();
    }
  }

  _resizePoints() {
    const aligneeCamera = windowManager.getWindow(windowManager.ALIGNEE).renderGraph.getInterfaceScene().camera;
    const alignerCamera = windowManager.getWindow(windowManager.ALIGNER).renderGraph.getInterfaceScene().camera;
    let updated = this._updateScales(aligneeCamera);
    updated = this._updateScales(alignerCamera) || updated;

    return updated;
  }

  _resizeHandler() {
    const updated = this._resizePoints();
    if (updated) {
      this._reportPoints();
    }
  }

  _enablePopupHandler(object, isAligner) {
    const popupPicks = isAligner ? this._alignerPicks : this._aligneePicks;
    const popupPick = popupPicks.find(pick => pick.drawable === object);
    events.emit("alignPointHovered", {"isAligner": isAligner, "index": popupPick.hash, "isHovered": true, "isContextMenuRequested": true, "isSelected": false});
    this._lastReportedPopup.object = object;
    this._lastReportedPopup.isAligner = isAligner;

    if (this._lastReportedSelection.object) {
      const selectionPicks = this._lastReportedSelection.isAligner ? this._alignerPicks : this._aligneePicks;
      const selectionPick = selectionPicks.find(pick => pick.drawable === this._lastReportedSelection.object);

      events.emit("alignPointHovered", {"isAligner": this._lastReportedSelection.isAligner, "index": selectionPick.hash, "isHovered": false, "isContextMenuRequested": false, "isSelected": false});
      selectionPick.size = DEFAULT_SPHERE_SIZE;
      this._resizePoints();
      const selectionWindow = this._lastReportedSelection.isAligner ?
        windowManager.getWindow(windowManager.ALIGNER):
        windowManager.getWindow(windowManager.ALIGNEE);
      selectionWindow.renderGraph.requestFrame();
      this._lastReportedSelection.object = null;
    }
  }

  _selectionHandler(object, isAligner) {
    const selectionWindow = isAligner ?
      windowManager.getWindow(windowManager.ALIGNER):
      windowManager.getWindow(windowManager.ALIGNEE);

    if (this._lastReportedSelection.object !== null) {
      const selectionPicks = this._lastReportedSelection.isAligner ? this._alignerPicks : this._aligneePicks;
      const selectionPick = selectionPicks.find(pick => pick.drawable === this._lastReportedSelection.object);

      if (this._lastReportedSelection.object === object) {
        events.emit("alignPointHovered", {"isAligner": this._lastReportedSelection.isAligner, "index": selectionPick.hash, "isHovered": true, "isContextMenuRequested": false, "isSelected": false});
        this._lastReportedSelection.object = null;
        return;
      } else {
        events.emit("alignPointHovered", {"isAligner": this._lastReportedSelection.isAligner, "index": selectionPick.hash, "isHovered": false, "isContextMenuRequested": false, "isSelected": false});
        selectionPick.size = DEFAULT_SPHERE_SIZE;
        this._resizePoints();
        selectionWindow.renderGraph.requestFrame();
      }
    }

    const hoverPicks = isAligner ? this._alignerPicks : this._aligneePicks;
    const hoverPick = hoverPicks.find(pick => pick.drawable === object);
    events.emit("alignPointHovered", {"isAligner": isAligner, "index": hoverPick.hash, "isHovered": true, "isContextMenuRequested": false, "isSelected": true});
    this._lastReportedSelection.object = object;
    this._lastReportedSelection.isAligner = isAligner;
  }

  _moveHandler(screenX, screenY) {
    const selectionWindow = this._lastReportedSelection.isAligner ?
      windowManager.getWindow(windowManager.ALIGNER):
      windowManager.getWindow(windowManager.ALIGNEE);

    if (!selectionWindow.isInViewport(screenX, screenY)) {
      return;
    }

    const camera = selectionWindow.renderGraph.getWorkspaceScene().camera;
    const sphereCenter = _VEC3_TMP2;
    selectionWindow.getPixelWorldPoint(screenX, screenY, camera, sphereCenter).then(res => {
      if (!res) {
        return;
      }

      const selectionPicks = this._lastReportedSelection.isAligner ? this._alignerPicks : this._aligneePicks;
      const selectionPick = selectionPicks.find(pick => pick.drawable === this._lastReportedSelection.object);
      const selectedObject = selectionPick.drawable;
      selectedObject.setTranslation(...sphereCenter);

      this._lastReportedSelection.object = null;
      this._reportPoints();
      selectionWindow.renderGraph.requestFrame();
    })
  }

  _disablePopupHandler() {
    if (this._lastReportedPopup.object === null) {
      return;
    }

    const popupWindow = this._lastReportedPopup.isAligner ?
      windowManager.getWindow(windowManager.ALIGNER):
      windowManager.getWindow(windowManager.ALIGNEE);

    const popupPicks = this._lastReportedPopup.isAligner ? this._alignerPicks : this._aligneePicks;
    const popupPick = popupPicks.find(pick => pick.drawable === this._lastReportedPopup.object);
    events.emit("alignPointHovered", {"isAligner": this._lastReportedPopup.isAligner, "index": popupPick.hash, "isHovered": false, "isContextMenuRequested": false, "isSelected": false});

    popupPick.size = DEFAULT_SPHERE_SIZE;
    this._lastReportedPopup.object = null;
    this._resizePoints();
    popupWindow.renderGraph.requestFrame();
  }

  _pickHandler(screenX, screenY) {

    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);
    let thisWindow = null;
    let thisPicks = null;
    let anotherPicks = null;

    if (aligneeWindow.isInViewport(screenX, screenY)) {
      thisWindow = aligneeWindow;
      thisPicks = this._aligneePicks;
      anotherPicks = this._alignerPicks;
    } else if (alignerWindow.isInViewport(screenX, screenY)) {
      thisWindow = alignerWindow;
      thisPicks = this._alignerPicks;
      anotherPicks = this._aligneePicks;
    } else {
      return;
    }

    const interfaceScene = thisWindow.renderGraph.getInterfaceScene();
    if (thisPicks.length > anotherPicks.length) {
      events.emit("alignPointRejected", {"isAligner": thisWindow === alignerWindow});
      return;
    }

    const camera = thisWindow.renderGraph.getWorkspaceScene().camera;
    const sphereCenter = _VEC3_TMP2;
    thisWindow.getPixelWorldPoint(screenX, screenY, camera, sphereCenter).then(res => {
      if (!res) {
        return;
      }
      const sphere = this._createDrawModel(sphereCenter);
      this._updateScale(sphere, DEFAULT_SPHERE_SIZE, camera);
      sphere.updateMatrix();
      const newColorHash = this._getNextColorHash(thisPicks, anotherPicks);

      const newPick = new Pick();
      newPick.drawable = sphere;
      newPick.size = DEFAULT_SPHERE_SIZE;
      newPick.hash = newColorHash;
      vec3.copy(sphere.properties.diffuseColor, colorUtils.generateColorInSequence(newColorHash));

      interfaceScene.add(sphere);
      thisPicks.push(newPick);

      if (this._lonelyPoint.object) {
        this._lonelyPoint.object = null;
      } else {
        this._lonelyPoint.object = sphere;
        this._lonelyPoint.isAligner = thisWindow === alignerWindow;
      }

      this._reportPoints();
      thisWindow.renderGraph.requestFrame();
    });
  }

  _createDrawModel(position) {

    const drawModel = objFactory.createDrawable(this._sphereShader);

    drawModel.addGeometryChunk(this._sphereGeometry);
    drawModel.copyTranslation(position);
    drawModel.setDiffuseColor(SPHERE_COLOR[0], SPHERE_COLOR[1], SPHERE_COLOR[2]);

    return drawModel;

  }

  _updateScale(object, size, camera) {
    const scale = screenUtils.calculateObjectScale(size, 2, object.trans, camera);
    object.setScale(scale, scale, scale);
  }

  _updateScales(camera) {
    let picks = null;
    const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
    const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);
    if (!aligneeWindow || !alignerWindow) {
      return false;
    }

    const aligneeScene = aligneeWindow.renderGraph.getInterfaceScene();
    const alignerScene = alignerWindow.renderGraph.getInterfaceScene();

    if (camera === aligneeScene.camera) {
      picks = this._aligneePicks;
    }

    if (camera === alignerScene.camera) {
      picks = this._alignerPicks;
    }

    if (!picks) {
      return false;
    }

    for (const pick of picks) {
      this._updateScale(pick.drawable, pick.size, camera);
    }

    return true;

  }

  _undoHandler() {
    this.reset();
  }

  _redoHandler() {
    this.reset();
  }

  cleanup() {
    this._lastReportedHover.object = null;
    this._lastReportedPopup.object = null;
    this._lastReportedSelection.object = null;
    this._dragging.object = null;
    super.cleanup();
  }

  _getPointsData() {
    const leftPoints = [];
    for (const pick of this._aligneePicks){
      const translation = pick.drawable.getTranslation(_VEC3_TMP);
      leftPoints.push({position: [translation[0], translation[1], translation[2]], color: pick.drawable.properties.diffuseColor, hash: pick.hash});
    }

    const rightPoints = [];
    for (const pick of this._alignerPicks){
      const translation = pick.drawable.getTranslation(_VEC3_TMP);
      rightPoints.push({position: [translation[0], translation[1], translation[2]], color: pick.drawable.properties.diffuseColor, hash: pick.hash});
    }

    return [leftPoints, rightPoints];
  }

  _getPointsCoords() {
    const leftPoints = [];
    const rightPoints = [];
    for (const leftPick of this._aligneePicks){
      const rightPick = this._alignerPicks.find(p => p.hash === leftPick.hash);
      if (rightPick) {
        const leftTranslation = leftPick.drawable.getTranslation(_VEC3_TMP);
        leftPoints.push([leftTranslation[0], leftTranslation[1], leftTranslation[2]]);
        const rightTranslation = rightPick.drawable.getTranslation(_VEC3_TMP);
        rightPoints.push([rightTranslation[0], rightTranslation[1], rightTranslation[2]]);
      }
    }

    return [leftPoints, rightPoints];
  }

  _getNextColorHash(thisPicks, otherPicks) {
    if (thisPicks.length === otherPicks.length) {
      const usedHashes = new Set();
      for (const pick of thisPicks) {
        usedHashes.add(pick.hash);
      }

      let i = 0;
      while (usedHashes.has(i)) {
        i++;
      }

      return i;
    } else {
      for (const otherPick of otherPicks) {
        const thisPointHash = thisPicks.find(pick => pick.hash === otherPick.hash);
        if (!thisPointHash) {
          return otherPick.hash;
        }
      }

      output.error(output.DEVELOPERS_ERROR, "Can't find point for pairing");
    }
  }

}
